import React, { useEffect, useMemo, useState } from 'react';
import Header from './Header';
import { apiService, PrimeReport } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface PrimeReportsResponse {
  success: boolean;
  available_reports: PrimeReport[];
  purchased_reports: PrimeReport[];
  has_org: boolean;
  error?: string;
}

const PrimeReportsView: React.FC = () => {
  const [availableReports, setAvailableReports] = useState<PrimeReport[]>([]);
  const [purchasedReports, setPurchasedReports] = useState<PrimeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchPurchased, setSearchPurchased] = useState('');
  const [hasOrg, setHasOrg] = useState(true);
  const [purchasingId, setPurchasingId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = (await apiService.fetchPrimeReports()) as PrimeReportsResponse;
        if (result.success) {
          setAvailableReports(result.available_reports || []);
          setPurchasedReports(result.purchased_reports || []);
          setHasOrg(result.has_org);
        } else {
          setError(result.error || 'Failed to load archived reports');
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load archived reports');
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, []);

  const filteredAvailable = useMemo(() => {
    if (!searchAvailable.trim()) return availableReports;
    const term = searchAvailable.toLowerCase();
    return availableReports.filter(
      (report) =>
        report.title.toLowerCase().includes(term) ||
        (report.description || '').toLowerCase().includes(term)
    );
  }, [availableReports, searchAvailable]);

  const filteredPurchased = useMemo(() => {
    if (!searchPurchased.trim()) return purchasedReports;
    const term = searchPurchased.toLowerCase();
    return purchasedReports.filter((report) => report.title.toLowerCase().includes(term));
  }, [purchasedReports, searchPurchased]);

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handlePurchase = async (report: PrimeReport) => {
    if (!hasOrg) return;
    try {
      setPurchasingId(report.id);
      const result = await apiService.purchasePrimeReport(report.id);
      if (result?.error) {
        throw new Error(result.error);
      }
      if (result?.url) {
        navigate(result.url);
        return;
      }
      navigate(`/prime-reports/${report.id}`);
      return;
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to purchase report');
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-10">
        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
          <h1 className="text-3xl font-bold text-slate-900">Archived Reports</h1>
          <p className="text-slate-500 text-sm mt-2">Older curated reports kept for reference.</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-center text-rose-600 font-medium">{error}</p>
          </div>
        ) : (
          <>
            {!!filteredPurchased.length && (
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {hasOrg ? "Organization's Ordered Reports" : 'Your Ordered Reports'}
                  </h2>
                  <input
                    value={searchPurchased}
                    onChange={(e) => setSearchPurchased(e.target.value)}
                    placeholder="Search ordered reports..."
                    className="w-full max-w-md px-4 py-2 text-sm border border-slate-200 rounded-full focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
                <div className="bg-white rounded-[2rem] shadow-card border border-emerald-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-emerald-50 border-b border-emerald-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">Report Title</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">Order Date</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">Ordered By</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-emerald-700 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-emerald-100">
                        {filteredPurchased.map((report) => (
                          <tr key={report.id} className="hover:bg-emerald-50">
                            <td className="px-6 py-4 text-sm font-semibold text-emerald-900">{report.title}</td>
                            <td className="px-6 py-4 text-sm text-emerald-700">{formatDate(report.purchased_at)}</td>
                            <td className="px-6 py-4 text-sm text-emerald-700">{report.purchased_by || '—'}</td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => navigate(`/prime-reports/${report.id}`)}
                                className="px-4 py-2 text-xs font-semibold rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                              >
                                View Report
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {!hasOrg && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800">
                <strong>Note:</strong> You need to be part of an organization to purchase reports. Please contact your
                administrator.
              </div>
            )}

            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Available Reports</h2>
                <input
                  value={searchAvailable}
                  onChange={(e) => setSearchAvailable(e.target.value)}
                  placeholder="Search reports..."
                  className="w-full max-w-md px-4 py-2 text-sm border border-slate-200 rounded-full focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>
              {filteredAvailable.length ? (
                <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Report Title</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredAvailable.map((report) => (
                          <tr key={report.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4 text-sm font-semibold text-slate-900">{report.title}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">{report.description}</td>
                            <td className="px-6 py-4 text-center">
                              <button
                                disabled={!hasOrg || purchasingId === report.id}
                                onClick={() => handlePurchase(report)}
                                className={`px-4 py-2 text-xs font-semibold rounded-full ${
                                  !hasOrg
                                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                }`}
                              >
                                {purchasingId === report.id ? 'Ordering...' : hasOrg ? 'Order' : 'Org Required'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 text-center text-slate-500">
                  No reports available at this time.
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default PrimeReportsView;
